import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { 
  ChevronDown, 
  ChevronRight, 
  Folder, 
  FolderOpen, 
  MapPin, 
  Trash2, 
  X,
  Lock,
  Unlock,
  Loader2,
  Link2,
  Users
} from 'lucide-react';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import type { D2DArea } from '@/types/d2d';
import type { AreaFolder } from '@/hooks/useAreaFolders';

interface AreaFolderSectionProps {
  folder: AreaFolder;
  areas: D2DArea[];
  unitCounts: Record<string, number>;
  tagCounts: Record<string, { BK: number; HOT: number; NA: number }>;
  sharedAreaActiveStatus: Record<string, boolean>;
  shareCounts: Record<string, number>;
  activatingAreaId: string | null;
  userId?: string;
  onSelectArea: (area: D2DArea) => void;
  onToggleExpanded: (folderId: string, isExpanded: boolean) => void;
  onDeleteFolder: (folderId: string, deleteAreas?: boolean) => void;
  onRemoveArea: (folderId: string, areaId: string) => void;
  onToggleAreaActive: (areaId: string, currentActive: boolean) => void;
  onToggleSharedAreaActive: (areaId: string, currentActive: boolean) => void;
  onActivateAndNavigate: (area: D2DArea) => void;
  onOpenSharingDialog: (areaId: string, areaName: string) => void;
}

export const AreaFolderSection = ({
  folder,
  areas,
  unitCounts,
  tagCounts,
  sharedAreaActiveStatus,
  shareCounts,
  activatingAreaId,
  userId,
  onSelectArea,
  onToggleExpanded,
  onDeleteFolder,
  onRemoveArea,
  onToggleAreaActive,
  onToggleSharedAreaActive,
  onActivateAndNavigate,
  onOpenSharingDialog
}: AreaFolderSectionProps) => {
  const [deleteAreasChecked, setDeleteAreasChecked] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  
  // Get areas that belong to this folder
  const folderAreas = areas.filter(area => folder.area_ids.includes(area.id));
  
  const handleDeleteFolder = () => {
    onDeleteFolder(folder.id, deleteAreasChecked);
    setIsDeleteDialogOpen(false);
    setDeleteAreasChecked(false);
  };
  
  if (folderAreas.length === 0) {
    return null; // Don't show empty folders
  }

  return (
    <div className="space-y-2">
      {/* Folder Header */}
      <div 
        className="flex items-center gap-2 p-3 rounded-lg bg-muted/50 cursor-pointer hover:bg-muted transition-colors"
        onClick={() => onToggleExpanded(folder.id, !folder.is_expanded)}
      >
        <Button
          variant="ghost"
          size="sm"
          className="h-6 w-6 p-0"
          onClick={(e) => {
            e.stopPropagation();
            onToggleExpanded(folder.id, !folder.is_expanded);
          }}
        >
          {folder.is_expanded ? (
            <ChevronDown className="h-4 w-4" />
          ) : (
            <ChevronRight className="h-4 w-4" />
          )}
        </Button>
        
        {folder.is_expanded ? (
          <FolderOpen className="h-5 w-5 text-primary" />
        ) : (
          <Folder className="h-5 w-5 text-primary" />
        )}
        
        <span className="font-medium flex-1">{folder.name}</span>
        
        <Badge variant="secondary" className="text-xs">
          {folderAreas.length} {folderAreas.length === 1 ? 'Gebiet' : 'Gebiete'}
        </Badge>
        
        <AlertDialog open={isDeleteDialogOpen} onOpenChange={(open) => {
          setIsDeleteDialogOpen(open);
          if (!open) setDeleteAreasChecked(false);
        }}>
          <AlertDialogTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className="h-6 w-6 p-0 text-muted-foreground hover:text-destructive"
              onClick={(e) => e.stopPropagation()}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent onClick={(e) => e.stopPropagation()}>
            <AlertDialogHeader>
              <AlertDialogTitle>Ordner löschen?</AlertDialogTitle>
              <AlertDialogDescription asChild>
                <div className="space-y-4">
                  <p>
                    Der Ordner "{folder.name}" wird gelöscht. Die Gebiete selbst bleiben standardmäßig erhalten.
                  </p>
                  
                  <div 
                    className="flex items-start gap-3 p-3 rounded-lg border border-muted bg-muted/30 cursor-pointer hover:bg-muted/50 transition-colors"
                    onClick={() => setDeleteAreasChecked(!deleteAreasChecked)}
                  >
                    <Checkbox 
                      id="delete-areas-checkbox"
                      checked={deleteAreasChecked}
                      onCheckedChange={(checked) => setDeleteAreasChecked(checked === true)}
                      onClick={(e) => e.stopPropagation()}
                    />
                    <div className="space-y-1">
                      <Label 
                        htmlFor="delete-areas-checkbox" 
                        className="text-sm font-medium text-destructive cursor-pointer"
                      >
                        Gebiete mit löschen
                      </Label>
                      <p className="text-xs text-muted-foreground">
                        Achtung: Löscht auch alle {folderAreas.length} Gebiete mit allen Straßen, Häusern und Wohneinheiten unwiderruflich!
                      </p>
                    </div>
                  </div>
                </div>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Abbrechen</AlertDialogCancel>
              <AlertDialogAction 
                onClick={handleDeleteFolder}
                className={deleteAreasChecked ? 'bg-destructive hover:bg-destructive/90' : ''}
              >
                {deleteAreasChecked ? 'Ordner & Gebiete löschen' : 'Nur Ordner löschen'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>

      {/* Folder Content */}
      {folder.is_expanded && (
        <div className="ml-4 grid gap-3 md:grid-cols-2 lg:grid-cols-3">
          {folderAreas.map((area) => {
            const isOwnArea = area.created_by === userId;
            const isActive = isOwnArea ? area.is_active : (sharedAreaActiveStatus[area.id] !== false);
            
            return (
              <Card 
                key={area.id} 
                className={`relative hover:shadow-md transition-all ${!isActive ? 'opacity-50' : ''}`}
              >
                {/* Remove from folder button */}
                <Button
                  variant="ghost"
                  size="sm"
                  className="absolute top-1 right-1 h-6 w-6 p-0 text-muted-foreground hover:text-destructive z-10"
                  onClick={(e) => {
                    e.stopPropagation();
                    onRemoveArea(folder.id, area.id);
                  }}
                  title="Aus Ordner entfernen"
                >
                  <X className="h-3 w-3" />
                </Button>

                <div 
                  className="cursor-pointer p-3"
                  onClick={() => onActivateAndNavigate(area)}
                >
                  <div className="flex flex-col items-center text-center">
                    {/* Lock/Unlock and Share buttons */}
                    <div className="flex gap-1 mb-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0 hover:bg-transparent"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (isOwnArea) {
                            onToggleAreaActive(area.id, area.is_active);
                          } else {
                            onToggleSharedAreaActive(area.id, sharedAreaActiveStatus[area.id] !== false);
                          }
                        }}
                        disabled={activatingAreaId === area.id}
                      >
                        {activatingAreaId === area.id ? (
                          <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />
                        ) : isActive ? (
                          <Unlock className={`h-3 w-3 ${isOwnArea ? 'text-green-600' : 'text-blue-500'}`} />
                        ) : (
                          <Lock className={`h-3 w-3 ${isOwnArea ? 'text-red-600' : 'text-gray-400'}`} />
                        )}
                      </Button>
                      
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0 hover:bg-transparent"
                        onClick={(e) => {
                          e.stopPropagation();
                          onOpenSharingDialog(area.id, area.name);
                        }}
                        title={!isOwnArea ? "Mit dir geteilt" : "Gebiet mit Teammitgliedern teilen"}
                      >
                        {!isOwnArea ? (
                          <Users className="h-3 w-3 text-blue-500" />
                        ) : shareCounts[area.id] > 0 ? (
                          <Link2 className="h-3 w-3 text-blue-500" />
                        ) : (
                          <Link2 className="h-3 w-3 text-primary" />
                        )}
                      </Button>
                    </div>

                    <div className="flex items-center gap-1.5 justify-center">
                      <MapPin className="h-3 w-3 text-primary shrink-0" />
                      <span className="font-medium text-sm truncate">{area.name}</span>
                    </div>
                    {area.plz && (
                      <p className="text-xs text-muted-foreground">{area.plz}</p>
                    )}
                    <p className="text-xs text-muted-foreground">
                      {unitCounts[area.id] || 0} WE
                    </p>
                  </div>

                  {/* Tag counts */}
                  <div className="flex gap-1 mt-2 justify-center">
                    <span className="text-[9px] px-1 py-0.5 rounded bg-orange-500/10 text-orange-600">
                      BK {tagCounts[area.id]?.BK || 0}
                    </span>
                    <span className="text-[9px] px-1 py-0.5 rounded bg-red-500/10 text-red-600">
                      HOT {tagCounts[area.id]?.HOT || 0}
                    </span>
                    <span className="text-[9px] px-1 py-0.5 rounded bg-yellow-500/10 text-yellow-600">
                      NA {tagCounts[area.id]?.NA || 0}
                    </span>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};
